'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
} from '@mui/material';
import Link from 'next/link';
import { Icon } from '@iconify/react';
import ArrowBackIcon from '@/letsgo/icons/ArrowBack';
import { getToolIconForPath } from '@/lib/toolsData';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { useRouter, useSearchParams } from 'next/navigation';
import { prefillCreatePage } from '@/lib/createPagePrefill';
import { useAiActionCooldown } from '@/hooks/useAiActionCooldown';

const EXAMPLE_PROMPTS = [
  'Instagram caption for a product launch',
  'Engagement post for a fitness brand',
  'Short tweet thread about a new feature',
  'LinkedIn post for a career milestone',
];

const PLATFORMS = ['any', 'Instagram', 'Twitter/X', 'LinkedIn', 'TikTok', 'Blog'];
const TONES = ['balanced', 'professional', 'casual', 'friendly', 'inspirational'];
const LENGTHS = ['short', 'medium', 'long'];

const PROVIDERS = [
  { value: 'gemini', label: 'Gemini (Google)' },
  { value: 'groq', label: 'Groq' },
] as const;

const VALID_TONES = ['balanced', 'professional', 'casual', 'friendly', 'inspirational'];

export default function AIContentCreationPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [prompt, setPrompt] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [platform, setPlatform] = useState('any');
  const [tone, setTone] = useState('balanced');
  const [length, setLength] = useState('medium');
  const [loading, setLoading] = useState(false);
  const contentAi = useAiActionCooldown(4000);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<'gemini' | 'groq'>('gemini');
  const [variants, setVariants] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [stubReason, setStubReason] = useState<string | null>(null);

  useEffect(() => {
    const t = searchParams?.get?.('tone');
    if (t && VALID_TONES.includes(t)) setTone(t);
    const vs = searchParams?.get?.('voiceSummary');
    if (vs) {
      try {
        const decoded = decodeURIComponent(vs);
        if (decoded) setPrompt((p) => p ? p : `Content in this voice: ${decoded}`);
      } catch (_) {}
    }
  }, [searchParams]);

  const handleGenerate = () => {
    void contentAi.run(async () => {
      setError(null);
      setStubReason(null);
      setVariants([]);
      setLoading(true);
      try {
        const res = await fetch('/api/tools/ai-content-creation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt, platform, tone, length, provider }),
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          throw new Error(data.error || 'Generation failed');
        }
        setVariants(Array.isArray(data.variants) ? data.variants : []);
        setSelectedIndex(0);
        if (data.stub && data.stubReason) setStubReason(data.stubReason);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Generation failed');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleEditInCreate = () => {
    const text = variants[selectedIndex];
    if (text) {
      prefillCreatePage({ content: text });
      router.push('/dashboard/create');
    }
  };

  const handleCopy = async () => {
    const text = variants[selectedIndex];
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const handleCheckTone = () => {
    const text = variants[selectedIndex];
    if (typeof window !== 'undefined' && text) {
      try {
        sessionStorage.setItem('voiceTonePrefill', text);
      } catch (_) {}
      router.push('/tools/voice-tone-analyzer');
    }
  };

  const currentVariant = variants[selectedIndex];

  return (
    <Box component="main" id="main-content" sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 12, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton component={Link} href="/tools" aria-label="Back to Tools"><ArrowBackIcon /></IconButton>
        <Icon icon={getToolIconForPath('/tools/ai-content-creation')} width={34} height={34} />
        <Typography variant="h4">AI Content Creation</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe what you want to create and get AI-generated content.
      </Typography>

      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        placeholder="What do you want to create? e.g. Instagram caption for a product launch"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        sx={{ mb: 1 }}
        inputProps={{ 'aria-label': 'What do you want to create?' }}
      />

      <Button
        type="button"
        fullWidth
        onClick={() => setMoreOpen((o) => !o)}
        endIcon={moreOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ minHeight: 44, mb: 1, justifyContent: 'space-between' }}
        aria-expanded={moreOpen}
        aria-controls="more-options"
      >
        More options
      </Button>
      <Collapse in={moreOpen}>
        <Stack id="more-options" spacing={2} sx={{ mb: 2, pt: 0 }}>
          <FormControl fullWidth size="medium">
            <InputLabel>Provider</InputLabel>
            <Select value={provider} label="Provider" onChange={(e) => setProvider(e.target.value as 'gemini' | 'groq')}>
              {PROVIDERS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Platform</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
              {PLATFORMS.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  onClick={() => setPlatform(p)}
                  variant={platform === p ? 'filled' : 'outlined'}
                  sx={{ minHeight: 44 }}
                />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Tone</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
              {TONES.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  onClick={() => setTone(t)}
                  variant={tone === t ? 'filled' : 'outlined'}
                  sx={{ minHeight: 44 }}
                />
              ))}
            </Stack>
          </Box>
          <FormControl fullWidth size="medium">
            <InputLabel>Length</InputLabel>
            <Select value={length} label="Length" onChange={(e) => setLength(e.target.value)}>
              {LENGTHS.map((l) => (
                <MenuItem key={l} value={l}>{l}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Collapse>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Example prompts</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap sx={{ mb: 2 }}>
        {EXAMPLE_PROMPTS.map((example) => (
          <Chip
            key={example}
            label={example}
            onClick={() => setPrompt(example)}
            sx={{ minHeight: 44 }}
            variant="outlined"
          />
        ))}
      </Stack>

      <Button
        type="button"
        variant="contained"
        fullWidth
        onClick={handleGenerate}
        disabled={loading || contentAi.disabled || !prompt.trim()}
        sx={{ minHeight: 44, mb: 2 }}
        aria-label="Generate content"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate'}
      </Button>

      {error && (
        <>
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>
          <Button type="button" variant="outlined" fullWidth sx={{ minHeight: 44, mb: 2 }} onClick={handleGenerate} disabled={loading || contentAi.disabled}>
            Retry
          </Button>
        </>
      )}

      {stubReason && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1 }}>
          {stubReason} Placeholder content is shown. Add the API key to your environment to enable AI generation.
        </Typography>
      )}
      {variants.length > 0 && (
        <Stack spacing={2} sx={{ mt: 3 }}>
          {variants.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                Variant {selectedIndex + 1} of {variants.length}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {variants.map((_, i) => (
                  <Button
                    type="button"
                    key={i}
                    size="small"
                    variant={i === selectedIndex ? 'contained' : 'outlined'}
                    onClick={() => setSelectedIndex(i)}
                    sx={{ minWidth: 40, minHeight: 44 }}
                    aria-label={`Variant ${i + 1}`}
                  >
                    {i + 1}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
          <Paper sx={{ p: 2 }}>
            <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {currentVariant}
            </Typography>
          </Paper>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Button
              type="button"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              sx={{ minHeight: 44 }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditInCreate}
              sx={{ minHeight: 44 }}
            >
              Edit in Create
            </Button>
            <Button
              type="button"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleGenerate}
              disabled={loading || contentAi.disabled}
              sx={{ minHeight: 44 }}
            >
              Try again
            </Button>
            <Button
              type="button"
              variant="outlined"
              startIcon={<RecordVoiceOverIcon />}
              onClick={handleCheckTone}
              sx={{ minHeight: 44 }}
            >
              Check tone & consistency
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
